import { memo } from 'react';
import { GOAL_ART } from './goalArt';
import styles from './GoalIllustration.module.scss';

type GoalSubject =
  | string
  | null
  | undefined
  | { subcategory?: string | null; subcategory_ru?: string | null; category?: string | null; category_ru?: string | null };

/* Запасной подбор по русскому названию цели, если slug неизвестен. Порядок важен: частные правила выше общих. */
const NAME_RULES: Array<[RegExp, string]> = [
  [/егэ.*проф/i, 'podgotovka_k_ege_profilnyj'],
  [/егэ.*баз/i, 'podgotovka_k_ege_bazovyj'],
  [/огэ/i, 'podgotovka_k_oge'],
  [/гвэ/i, 'podgotovka_k_gve'],
  [/впр|подготовка к школе/i, 'podgotovka_k_shkole_i_vpr'],
  [/подготовка к олимп/i, 'podgotovka_k_olimpiadam'],
  [/олимп|углубл/i, 'olimpiadnaya_i_uglublennaya'],
  [/фундамент|профильный вуз/i, 'fundamentalnaya_matematika_profilynyj_vuz'],
  [/высшая/i, 'vysshaya_matematika_bazovyj_vuz'],
  [/поступлен/i, 'podgotovka_k_postupleniyu_v_vuz'],
  [/репетитор|учител/i, 'podgotovka_repetitorov_i_uchitelej'],
  [/студент/i, 'podgotovka_dlya_studentov'],
  [/успеваем/i, 'povyshenie_uspevaemosti'],
  [/методич|служебн/i, 'metodicheskie_bloki'],
  [/начала мат|школа\)/i, 'nachala_matematicheskogo_analiza_shkola'],
  [/анализ/i, 'matematicheskiy_analiz'],
  [/тригоном/i, 'trigonometriya'],
  [/вероятн|статист/i, 'teoriya_veroyatnostey'],
  [/начальн/i, 'nachalnaya_shkola'],
  [/арифмет|числа/i, 'arifmetika_i_chisla'],
  [/алгебр/i, 'algebra'],
  [/геометр/i, 'geometriya'],
];

export function resolveGoalArtKey(subject: GoalSubject): string {
  if (!subject) return 'fallback';
  const slug = typeof subject === 'string' ? subject : subject.subcategory || '';
  const key = slug.trim().toLowerCase();
  if (key && GOAL_ART[key]) return key;
  const name = typeof subject === 'string' ? subject : `${subject.subcategory_ru || ''} ${subject.subcategory || ''}`;
  const hit = NAME_RULES.find(([re]) => re.test(name));
  return hit ? hit[1] : 'fallback';
}

/** Декоративная иллюстрация цели занятия. Только фон: aria-hidden, pointer-events: none. */
export const GoalIllustration = memo(function GoalIllustration({
  subject,
  className = '',
  fit = 'contain',
}: {
  subject: GoalSubject;
  className?: string;
  /** contain - целиком, прижато вправо вниз; cover - заполняет весь блок (лишнее обрезается). */
  fit?: 'contain' | 'cover';
}) {
  const key = resolveGoalArtKey(subject);
  return (
    <svg
      className={`${styles.art} ${className}`}
      viewBox="0 0 160 120"
      preserveAspectRatio={fit === 'cover' ? 'xMidYMid slice' : 'xMaxYMax meet'}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-goal-art={key}
      dangerouslySetInnerHTML={{ __html: GOAL_ART[key] }}
    />
  );
});

export default GoalIllustration;
